import { useCallback, useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Slider from '@mui/material/Slider'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import api from 'services/api'
import useToast from 'hooks/useToast'
import LabChart, { LAB_COLORS } from './LabChart'
import { Legend, SectionHeading, Tile } from './parts'
import { dayLabel, fmtUsd, fmtWhen } from './vocabulary'

// Shares to one place: the lab's differences live in the first percent.
const pct = (value) =>
  value == null || !Number.isFinite(value)
    ? '—'
    : `${(value * 100).toFixed(1)}%`

// Hours as a person reads them: minutes under one.
const duration = (hours) => {
  if (hours == null) return '—'
  if (hours >= 100) return `${Math.round(hours).toLocaleString('en-US')} h`
  if (hours >= 1) return `${hours.toFixed(1)} h`
  return `${Math.round(hours * 60)} min`
}

const whole = (value) => Number(value || 0).toLocaleString('en-US')
const capital = (text) => text.charAt(0).toUpperCase() + text.slice(1)

// The lab's checks against what ran live (reclip-api `replay.checks`).
const CHECK_WORDS = {
  unreviewed: 'Unreviewed-quotes limit',
  credits: 'Per-user transcription bucket',
  allowance: 'Daily share of the pool and the ceiling',
  pool_credits: 'Credits sent to ElevenLabs',
}

const VERDICTS = {
  matches: { label: 'Matches', color: 'success' },
  differs: { label: "Doesn't match", color: 'error' },
  pending: { label: "Can't check yet", color: 'default' },
  quiet: { label: 'Never fired', color: 'default' },
}

// One line under a verdict, where it needs one.
const verdictNote = (check) => {
  if (check.verdict === 'pending')
    return check.since
      ? `The live counter started ${dayLabel(
          check.since
        )}, part-way through the day. It needs whole days after that.`
      : "The live counter hasn't counted anything yet."
  if (check.verdict === 'differs')
    return "Don't trust this one's numbers until the difference is explained."
  if (check.verdict === 'quiet')
    return 'Neither the replay nor the live counter ever stopped a run.'
  return null
}

const lower = (text) => text.charAt(0).toLowerCase() + text.slice(1)

// The checks folded into the closed row's one verdict: red when any check
// doesn't match, yellow while any can't be checked yet, green otherwise.
const trustOf = (checks) => {
  const named = (list) =>
    list.map(({ check }) => lower(CHECK_WORDS[check] || check)).join(' and ')
  const differs = checks.filter(({ verdict }) => verdict === 'differs')
  const pending = checks.filter(({ verdict }) => verdict === 'pending')
  if (differs.length)
    return {
      tone: 'error',
      label: "Doesn't match what ran live",
      sentence: `The ${named(differs)} ${
        differs.length > 1 ? "don't" : "doesn't"
      } match. Don't trust ${
        differs.length > 1 ? 'their' : 'its'
      } numbers until the difference is explained.`,
    }
  if (pending.length)
    return {
      tone: 'warning',
      label: 'Partly checked',
      sentence: `${checks.length - pending.length} of ${
        checks.length
      } checks match what ran live. The ${named(
        pending
      )} can't be checked yet.`,
    }
  return {
    tone: 'success',
    label: 'Matches what ran live',
    sentence: 'Every check the lab can make matches what ran live.',
  }
}

const checkValue = (check, value) =>
  check.unit === 'credits'
    ? `${whole(Math.round(value))} credits`
    : duration(value)

// How a lever runs today: the router enforces the floors, and the spend
// gates run in their mode on the Spend controls below.
const statusOf = (lever, controls) => {
  if (!lever.mode_control)
    return lever.live === 'enforcing'
      ? { label: 'Live', color: 'success' }
      : { label: 'Simulation only', color: 'default' }
  const mode = controls?.controls?.[lever.mode_control]?.value
  return mode === 'enforcing'
    ? { label: 'Enforcing', color: 'warning' }
    : { label: 'Shadow', color: 'default' }
}

// What Apply sets for one lever at one of its settings, or why it can't.
const applyOf = (lever, row) => {
  if (!lever.mode_control)
    return { blocked: "isn't a control yet; Terraform sets it" }
  const changes = { [lever.mode_control]: 'enforcing' }
  if (lever.knob_control) changes[lever.knob_control] = row.value
  else if (lever.unit !== 'on' && row.value !== lever.today)
    return {
      blocked: `can only be applied at today's ${lever.today_setting}; Terraform sets the rest`,
    }
  return { changes }
}

// The four outcomes for one setting or one plan, read against the window.
const Outcome = ({ row, base }) => {
  const cash = row.money
  const above = cash && cash.overage_usd_day > 0
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
      <Tile
        label="Transcription stopped"
        value={pct(row.stopped_share)}
        note={`${duration(row.stopped_hours_day)} a day`}
        help="The share of the audio sent to ElevenLabs in the window that this would not have sent."
      />
      <Tile
        label="Quotes lost"
        value={whole(row.lost)}
        note={`${pct(row.lost_share)} of ${whole(base.quotes)}`}
        sub={`${whole(
          row.lost_media
        )} flagged as media · users with friends lose ${pct(
          row.friends_lost_share
        )}`}
        help="A quote is lost when its words sit in a minute this stops. Media quotes are ones the media rule flags; the media gate would hide them anyway."
      />
      <Tile
        label="Spread through the day"
        value={row.parts_per_day.toFixed(2)}
        note={`parts of the day with a quote, from ${Number(
          base.parts_per_day || 0
        ).toFixed(2)}`}
        sub={`${whole(row.days_emptied)} user-days would lose every quote`}
        help="Per user-day on the user's own clock: how many of night, morning, afternoon and evening still get a quote. A budget that runs out by noon shows here."
      />
      <Tile
        label="Money"
        value={
          cash
            ? above
              ? `${fmtUsd(cash.overage_usd_day)} a day`
              : '$0.00'
            : '—'
        }
        note={
          !cash
            ? 'The pool is not known.'
            : above
            ? 'off the ElevenLabs overage'
            : 'inside the prepaid pool, the credits it saves would go unused'
        }
        sub={
          cash
            ? `${whole(
                Math.round(cash.credits_saved_day)
              )} credits a day · Gemini about ${fmtUsd(
                cash.gemini_usd_day
              )} a day`
            : undefined
        }
        help="ElevenLabs is prepaid up to the monthly pool, so a cut saves dollars only where it takes spend off a cycle running past the pool. Priced over a full cycle at the last week's pace."
      />
    </Box>
  )
}

// The cost lab: every spend lever priced over the last whole days, from the
// job that runs every 6 hours (reclip-api docs/audio-streaming/cost-lab.md).
// One lever at a time on its curve, then levers together as a plan, replayed
// in the order the pipeline applies them, and Apply through the spend
// controls. Every number comes from the API; the page reads it (`lab`, or
// `error` when the read failed) and shows its first sentence at the top.
const Lab = ({ lab, error, controls, onSaved }) => {
  const toast = useToast()
  const [budget, setBudget] = useState('0.02')
  const [selected, setSelected] = useState(null)
  const [positions, setPositions] = useState({})
  const [plan, setPlan] = useState({})
  const [replayed, setReplayed] = useState(null)
  const [replayError, setReplayError] = useState(null)
  const [replaying, setReplaying] = useState(false)
  const [dialog, setDialog] = useState(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [trustOpen, setTrustOpen] = useState(false)

  const ready = Boolean(lab?.ready)
  const levers = useMemo(() => (ready ? lab.levers : []), [ready, lab])
  const headline = ready
    ? lab.headlines[budget] || Object.values(lab.headlines)[0]
    : null
  const bestOf = useMemo(
    () =>
      Object.fromEntries(
        (headline?.ranking || []).map((entry) => [entry.key, entry])
      ),
    [headline]
  )

  // A lever opens at its best setting within the budget, or at its first.
  const positionOf = useCallback(
    (lever) => {
      if (positions[lever.key] != null) return positions[lever.key]
      const best = bestOf[lever.key]
      const found = best
        ? lever.rows.findIndex((row) => row.value === best.value)
        : 0
      return Math.max(0, found)
    },
    [positions, bestOf]
  )

  const current =
    levers.find(({ key }) => key === selected) ||
    levers.find(({ key }) => key === headline?.ranking?.[0]?.key) ||
    levers[0]

  // The plan is replayed on the job's sample a moment after it settles.
  useEffect(() => {
    if (!Object.keys(plan).length) {
      setReplayed(null)
      setReplayError(null)
      return undefined
    }
    let live = true
    setReplaying(true)
    const timer = setTimeout(async () => {
      try {
        const out = await api.streamMetrics.replayLab(plan)
        if (live) {
          setReplayed(out)
          setReplayError(null)
        }
      } catch (err) {
        if (live) setReplayError(err.message)
      } finally {
        if (live) setReplaying(false)
      }
    }, 400)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [plan])

  const move = (lever, index) => {
    setPositions((held) => ({ ...held, [lever.key]: index }))
    setPlan((held) =>
      lever.key in held
        ? { ...held, [lever.key]: lever.rows[index].value }
        : held
    )
  }

  const include = (lever, on) =>
    setPlan((held) => {
      const next = { ...held }
      if (on) next[lever.key] = lever.rows[positionOf(lever)].value
      else delete next[lever.key]
      return next
    })

  const applyPlan = () => {
    const changes = {}
    const words = []
    const blocked = []
    levers
      .filter(({ key }) => key in plan)
      .forEach((lever) => {
        const row = lever.rows.find(({ value }) => value === plan[lever.key])
        const out = applyOf(lever, row)
        if (out.blocked) blocked.push(`${capital(lever.label)} ${out.blocked}.`)
        else {
          Object.assign(changes, out.changes)
          words.push(
            lever.unit === 'on'
              ? lever.label
              : `${lever.label} at ${row.setting}`
          )
        }
      })
    if (!Object.keys(changes).length) {
      toast({
        message: 'Nothing in this plan can be applied from here yet.',
        severity: 'info',
      })
      return
    }
    setReason('')
    setDialog({ changes, words, blocked })
  }

  const confirm = async () => {
    setSaving(true)
    try {
      const next = await api.streamControls.saveControls({
        changes: dialog.changes,
        reason: reason.trim(),
      })
      onSaved(next)
      setDialog(null)
      toast({
        message: 'Applied. It takes effect from the next pass.',
        severity: 'success',
      })
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const heading = (subtitle, actions) => (
    <SectionHeading
      title="What would a change save?"
      help="The cost lab replays the window as the pipeline ran it, with a lever moved, and counts what that would have stopped and lost. It can't tell how people would react to fewer quotes."
      subtitle={subtitle}
    >
      {actions}
    </SectionHeading>
  )

  if (error)
    return (
      <>
        {heading()}
        <Alert severity="error">{error}</Alert>
      </>
    )
  if (!lab) return <CircularProgress size={18} />
  if (!ready || !current)
    return heading(
      "The lab hasn't run yet. It runs every 6 hours, and the first run fills in the last two weeks."
    )

  const rows = current.rows
  const position = Math.min(positionOf(current), rows.length - 1)
  const row = rows[position]
  const inPlan = Object.keys(plan)
  const checks = lab.checks || []
  const trust = trustOf(checks)

  return (
    <>
      {heading(
        `Every lever, replayed over ${dayLabel(lab.window.from)} to ${dayLabel(
          lab.window.to
        )} UTC (${
          lab.window.days
        } whole days). A lever's best setting stops the most transcription while losing no more quotes than the share you pick. Updated ${fmtWhen(
          lab.as_of
        )}.`
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          A lever may lose up to
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={budget}
          onChange={(event, value) => value && setBudget(value)}
        >
          {lab.budgets.map((value) => (
            <ToggleButton key={value} value={String(value)}>
              {`${Math.round(value * 100)}%`}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          of quotes.
        </Typography>
      </Box>

      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 3 }}>
        {headline.sentences.map((sentence, index) => (
          <Typography
            key={sentence}
            variant={index === 1 ? 'body1' : 'body2'}
            fontWeight={index === 1 ? 700 : 400}
            color={
              index === 1 || index === 2 ? 'text.primary' : 'text.secondary'
            }
            sx={{ mb: index === headline.sentences.length - 1 ? 0 : 0.75 }}
          >
            {sentence}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            mobile: 'minmax(0, 1fr)',
            desktop: '320px minmax(0, 1fr)',
          },
          gap: 3,
          mb: 3,
        }}
      >
        <Box>
          {levers.map((lever) => {
            const status = statusOf(lever, controls)
            const best = bestOf[lever.key]
            const bestRow = best
              ? lever.rows.find(({ value }) => value === best.value)
              : null
            const on = lever.key === current.key
            return (
              <Paper
                key={lever.key}
                variant="outlined"
                onClick={() => setSelected(lever.key)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  cursor: 'pointer',
                  borderColor: on ? 'primary.main' : 'divider',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {capital(lever.label)}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={status.label}
                    color={status.color}
                  />
                  {lever.key in plan && (
                    <Chip size="small" color="primary" label="In my plan" />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ mt: 0.5 }}
                >
                  {bestRow
                    ? `Best within the budget: ${bestRow.setting}, stops ${pct(
                        bestRow.stopped_share
                      )} for ${whole(bestRow.lost)} quotes.`
                    : 'No setting stops anything within the budget.'}
                </Typography>
              </Paper>
            )
          })}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {capital(current.label)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, maxWidth: 820 }}
          >
            {current.sentence}
            {current.today_setting ? ` Today: ${current.today_setting}.` : ''}
          </Typography>
          <Legend
            items={[
              {
                key: 'stopped',
                label: 'Transcription stopped',
                color: LAB_COLORS.stopped,
              },
              { key: 'lost', label: 'Quotes lost', color: LAB_COLORS.lost },
              {
                key: 'not-media',
                label: 'Quotes lost, not media',
                color: LAB_COLORS.lost,
                dashed: true,
              },
            ]}
          />
          <LabChart lever={current} base={lab.base} chosen={position} />
          <Box sx={{ px: 1.5, mt: 1 }}>
            <Slider
              aria-label={`${current.label} setting`}
              value={position}
              min={0}
              max={Math.max(0, rows.length - 1)}
              step={1}
              marks
              valueLabelDisplay="auto"
              valueLabelFormat={(index) => rows[index]?.setting}
              onChange={(event, value) => move(current, value)}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2">
              At{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {row.setting}
              </Box>
              {row.stopped_by && Object.keys(row.stopped_by).length
                ? ` · stops ${whole(
                    Object.values(row.stopped_by).reduce((a, b) => a + b, 0)
                  )} runs`
                : ''}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={current.key in plan}
                  onChange={(event) => include(current, event.target.checked)}
                />
              }
              label="Add to my plan"
            />
          </Box>
          <Outcome row={row} base={lab.base} />
        </Box>
      </Box>

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: { mobile: 2, desktop: 3 },
          mb: 3,
        }}
      >
        <SectionHeading
          title="My plan"
          subtitle={`Levers together, in the order the pipeline applies them, so a run one lever stops draws nothing from the next. Replayed on ${whole(
            lab.sample.accounts
          )} of ${whole(lab.sample.of)} accounts.`}
        >
          {replaying && <CircularProgress size={16} />}
          <Button
            variant="contained"
            size="small"
            disabled={!inPlan.length}
            onClick={applyPlan}
          >
            Apply
          </Button>
        </SectionHeading>
        {!inPlan.length ? (
          <Typography variant="body2" color="text.secondary">
            Turn on “Add to my plan” for a lever to see levers together.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {levers
                .filter(({ key }) => key in plan)
                .map((lever) => (
                  <Chip
                    key={lever.key}
                    label={`${capital(lever.label)}: ${
                      lever.rows.find(({ value }) => value === plan[lever.key])
                        ?.setting
                    }`}
                    onClick={() => setSelected(lever.key)}
                    onDelete={() => include(lever, false)}
                  />
                ))}
            </Box>
            {replayError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {replayError}
              </Alert>
            )}
            {replayed && (
              <Outcome row={replayed.outcome} base={replayed.base} />
            )}
          </>
        )}
      </Box>

      <SectionHeading title="Can these numbers be trusted?" />
      {checks.length ? (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <ButtonBase
            onClick={() => setTrustOpen((open) => !open)}
            aria-expanded={trustOpen}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: `${trust.tone}.main`,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                {trust.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {trust.sentence}
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transform: trustOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </ButtonBase>
          <Collapse in={trustOpen}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pb: 1.5, maxWidth: 820 }}
            >
              Each spend gate already runs in shadow, counting the hours it
              would have stopped. The lab replays the same days with today's
              settings. When its count matches the live one, the lab's other
              numbers for that gate can be trusted. The last row checks the
              credits the replay sends against the meter.
            </Typography>

            {checks.map((check) => {
              const verdict = VERDICTS[check.verdict] || VERDICTS.pending
              const note = verdictNote(check)
              return (
                <Box
                  key={check.check}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'grid',
                    gridTemplateColumns: {
                      mobile: 'minmax(0, 1fr)',
                      desktop: 'minmax(0, 2fr) minmax(0, 2fr) minmax(0, 3fr)',
                    },
                    gap: { mobile: 0.75, desktop: 2 },
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {CHECK_WORDS[check.check] || check.check}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`Replayed ${checkValue(
                      check,
                      check.replayed
                    )} · counted live ${checkValue(check, check.live)}`}
                  </Typography>
                  <Box>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={verdict.label}
                      color={verdict.color}
                    />
                    {note && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.5 }}
                      >
                        {note}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Collapse>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          The checks appear after the lab's next run.
        </Typography>
      )}

      <Dialog
        open={Boolean(dialog)}
        onClose={() => !saving && setDialog(null)}
        maxWidth={false}
        PaperProps={{ sx: { width: '100%', maxWidth: 520 } }}
      >
        {dialog && (
          <>
            <DialogTitle>Apply this plan?</DialogTitle>
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Typography variant="body2">
                {`Enforce ${dialog.words.join(
                  ', '
                )}. Each applies from its next pass, and Spend controls below keeps the change.`}
              </Typography>
              {replayed && (
                <Alert
                  severity={
                    replayed.outcome.lost_share > 0.05 ? 'warning' : 'info'
                  }
                >
                  {`Over the window, the plan would have stopped ${pct(
                    replayed.outcome.stopped_share
                  )} of transcription and lost ${whole(
                    replayed.outcome.lost
                  )} quotes (${pct(replayed.outcome.lost_share)}).`}
                </Alert>
              )}
              {dialog.blocked.length > 0 && (
                <Alert severity="warning">
                  {`Not applied: ${dialog.blocked.join(' ')}`}
                </Alert>
              )}
              <TextField
                label="Why (kept in the change log)"
                multiline
                minRows={2}
                InputLabelProps={{ required: true }}
                autoFocus
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={confirm}
                disabled={saving || !reason.trim()}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                Apply
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

export default Lab
